import { useEffect, useState } from "react";
import Lottie from "lottie-react";
import {
  FaCheckCircle,
  FaCoffee,
  FaLeaf,
  FaSyncAlt,
  FaTint,
  FaBan,
  FaThermometerHalf,
  FaFire,
  FaHeartbeat,
  FaPowerOff,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  SectionCard,
  LiveBadge,
  Notice,
  CardGridSkeleton,
  BTN_SECONDARY,
} from "./MaintenanceUi";
import {
  LOTTIE,
  resolveCupView,
  resolvePotView,
  getPotProgramStatus,
  dirtinessLevel,
} from "./deviceStatusDisplay";

// Tabletteki / mobildeki durum renkleri — Tailwind paletinden.
const TONE = {
  danger: {
    fill: "bg-rose-500",
    ring: "border-rose-400",
    text: "text-rose-700",
    bg: "bg-rose-50",
  },
  water: {
    fill: "bg-cyan-400",
    ring: "border-cyan-400",
    text: "text-cyan-700",
    bg: "bg-cyan-50",
  },
  ok: {
    fill: "bg-emerald-400",
    ring: "border-emerald-400",
    text: "text-emerald-700",
    bg: "bg-emerald-50",
  },
  busy: {
    fill: "bg-orange-400",
    ring: "border-orange-400",
    text: "text-orange-700",
    bg: "bg-orange-50",
  },
  down: {
    fill: "bg-purple-500",
    ring: "border-purple-500",
    text: "text-purple-700",
    bg: "bg-purple-50",
  },
  wait: {
    fill: "bg-yellow-400",
    ring: "border-yellow-400",
    text: "text-yellow-700",
    bg: "bg-yellow-50",
  },
  heat: {
    fill: "bg-orange-500",
    ring: "border-orange-500",
    text: "text-orange-800",
    bg: "bg-orange-50",
  },
  idle: {
    fill: "bg-gray-300",
    ring: "border-gray-200",
    text: "text-gray-600",
    bg: "bg-white",
  },
};

// Tabletin ton adları → panel TONE anahtarı
const toneKey = (t) =>
  ({
    success: "ok",
    action: "danger",
    cancel: "busy",
    warning: "danger",
    info: "water",
    busy: "busy",
    idle: "idle",
  })[t] || t;

const ICONS = {
  check: FaCheckCircle,
  noWater: FaTint,
  quota: FaBan,
  tea: FaLeaf,
  coffee: FaCoffee,
};

function Indicator({ status, size = 44 }) {
  if (status.lottie && LOTTIE[status.lottie]) {
    return (
      <Lottie
        animationData={LOTTIE[status.lottie]}
        loop
        style={{ width: size, height: size }}
      />
    );
  }
  const Icon = ICONS[status.icon];
  if (!Icon) return <span style={{ width: size, height: size }} />;
  return (
    <span
      className={`flex items-center justify-center ${TONE[status.tone]?.text || ""}`}
      style={{ width: size, height: size }}
    >
      <Icon size={size * 0.55} />
    </span>
  );
}

function DirtBar({ counter, endCounter }) {
  const lvl = dirtinessLevel(counter, endCounter);
  if (!lvl) return null;
  const t = TONE[lvl.tone];
  return (
    <div className="mt-1.5">
      <div className="flex items-baseline justify-between gap-2 text-[11px] text-gray-500 whitespace-nowrap">
        <span className="truncate">Temizlik sayacı</span>
        <span className={`shrink-0 font-SemiBold tabular-nums ${t.text}`}>
          {counter}/{endCounter} · {lvl.label}
        </span>
      </div>
      <div className="h-1.5 rounded bg-gray-100 overflow-hidden mt-0.5">
        <div
          className={`h-full ${t.fill}`}
          style={{ width: `${Math.min(100, lvl.pct)}%` }}
        />
      </div>
    </div>
  );
}

// Bu süre içinde hiç durum gelmezse neden gelmediği söylenir.
const NO_DATA_AFTER_MS = 10000;

const fmtClock = (ms) => {
  if (ms <= 0) return "00:00";
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const fmtAgo = (ts, now) => {
  if (!ts) return null;
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s} sn önce`;
  return `${Math.floor(s / 60)} dk önce`;
};

function Countdown({ endsAt, now, label }) {
  if (!endsAt) return null;
  const ms = Date.parse(endsAt) - now;
  if (!Number.isFinite(ms)) return null;
  return (
    <div className="text-xs text-gray-700 mt-1">
      {label ? `${label}: ` : ""}
      <span className="font-Bold tabular-nums">{fmtClock(ms)}</span>
    </div>
  );
}

function CupCard({ index, cup, now }) {
  const status = resolveCupView(cup);
  const t = TONE[status.tone] || TONE.idle;
  const target = cup.targetTemperature ?? cup.temperatures;
  return (
    <div className={`rounded-xl border-2 ${t.ring} ${t.bg} p-2.5`}>
      <div className="flex items-center gap-2">
        <Indicator status={status} />
        <div className="min-w-0">
          <div className="text-xs text-gray-500">Göz {index}</div>
          <div className={`text-sm font-Bold ${t.text} leading-tight`}>
            {status.label}
          </div>
          {status.detail && (
            <div className="text-xs text-gray-600 truncate">
              {status.detail}
            </div>
          )}
          {status.showHeat && (
            <div className="text-xs text-gray-700 tabular-nums">
              {cup.heaterDegrees ?? "—"} / {target ?? "—"} °C
            </div>
          )}
          <Countdown endsAt={status.endsAt} now={now} />
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {cup.cupDisabled && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">
            Devre dışı
          </span>
        )}
        {cup.cupAlready && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
            Fincan yok devam et
          </span>
        )}
        {(cup.defaultPowerMode || cup.fullPowerMode) && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
            {cup.defaultPowerMode || cup.fullPowerMode}
          </span>
        )}
      </div>
      {!cup.cupAlready && (
        <DirtBar
          counter={cup.cleaningCounter}
          endCounter={cup.cleaningEndCounter}
        />
      )}
    </div>
  );
}

function PotCard({ potKey, pot, lowWater, potProgramInfo, now }) {
  const view = resolvePotView(pot, lowWater, potProgramInfo, potKey);
  const t = TONE[view.tone] || TONE.idle;
  // Eski tablet: geri sayım/program mobil kuralıyla
  const legacyProgram = view.legacy ? getPotProgramStatus(pot) : null;
  const b = pot?.brewInfo || {};
  return (
    <div className={`rounded-xl border-2 ${t.ring} ${t.bg} p-2.5`}>
      <div className="flex items-center gap-2">
        <Indicator status={view} />
        <div className="min-w-0">
          <div className="text-xs text-gray-500">
            Demlik {potKey}
            {view.productType ? ` · ${view.productType}` : ""}
          </div>
          <div className={`text-sm font-Bold ${t.text} leading-tight`}>
            {view.label}
            {view.stale && view.label !== "Bayat" && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 align-middle">
                Bayat
              </span>
            )}
          </div>
          {view.detail && (
            <div className="text-xs text-gray-600">{view.detail}</div>
          )}
          {view.remain && (
            <div className="text-xs text-gray-700 tabular-nums">
              Kalan {view.remain} bardak
            </div>
          )}
          <Countdown endsAt={view.endsAt} now={now} label="Hazır olmasına" />
        </div>
      </div>
      {view.programAt && (
        <div className="text-xs text-gray-700 mt-1.5">
          Demleme zamanı:{" "}
          <span className="font-SemiBold">
            {new Date(view.programAt).toLocaleString("tr-TR", {
              weekday: "long",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          {view.programText ? ` · ${view.programText}` : ""}
        </div>
      )}
      {legacyProgram?.kind === "countdown" && (
        <div className="text-xs text-gray-700 mt-1.5">
          {legacyProgram.info}:{" "}
          <span className="font-Bold tabular-nums">
            {fmtClock(legacyProgram.finishAt - now)}
          </span>
        </div>
      )}
      {view.mustClean && (
        <div className="text-xs text-rose-700 mt-1.5">
          Temizlik zamanı geldi
        </div>
      )}
      <DirtBar
        counter={
          b.cleaningCounter ??
          (b.type === "FILTERCOFFEE"
            ? b.cleaningCounterFilterCoffee
            : b.cleaningCounterTea)
        }
        endCounter={
          b.cleaningEndCounter ??
          (b.type === "FILTERCOFFEE"
            ? b.cleaningEndCounterFilterCoffee
            : b.cleaningEndCounterTea)
        }
      />
    </div>
  );
}

function TurkishTanksStrip({ tanks }) {
  if (!tanks || !Array.isArray(tanks.tanks)) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {tanks.tanks.map((t) => {
        const tone = TONE[toneKey(t.tone)] || TONE.idle;
        return (
          <div
            key={t.tank}
            className={`rounded-lg border ${tone.ring} ${tone.bg} px-3 py-2`}
          >
            <div className="text-[11px] text-gray-500 flex items-center gap-1">
              <FaTint /> Türk kahvesi deposu{" "}
              {tanks.tanks.length > 1 ? t.tank : ""}
            </div>
            <div className={`text-sm font-Bold ${tone.text}`}>
              {t.text}
              {tanks.pumpMode ? ` · ${tanks.pumpMode}` : ""}
            </div>
          </div>
        );
      })}
      {tanks.warning && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-Bold text-rose-700">
          {tanks.warning}
        </div>
      )}
    </div>
  );
}

function WaterTankStrip({ tank }) {
  if (!tank) return null;
  const hot =
    Number.isFinite(tank.temperature) &&
    Number.isFinite(tank.heatValue) &&
    tank.temperature >= tank.heatValue;
  const level = Number.isFinite(tank.waterLevel)
    ? Math.max(0, Math.min(100, Math.round(tank.waterLevel)))
    : null;
  return (
    <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-[11px] text-gray-500 flex items-center gap-1">
          <FaThermometerHalf /> Su kazanı
        </div>
        <div
          className={`text-sm font-Bold tabular-nums ${hot ? "text-emerald-700" : "text-gray-800"}`}
        >
          {tank.temperature ?? "—"} / {tank.heatValue ?? "—"} °C
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-[11px] text-gray-500 flex items-center gap-1">
          <FaFire /> {tank.display?.heaterLabel || "Isıtıcı"}
        </div>
        <div
          className={`text-sm font-Bold ${hot ? "text-emerald-700" : tank.heater ? "text-rose-700" : "text-gray-600"}`}
        >
          {tank.display?.heaterState || (tank.heater ? "Açık" : "Kapalı")}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-[11px] text-gray-500 flex items-center gap-1">
          <FaTint /> Su seviyesi
        </div>
        <div className="text-sm font-Bold tabular-nums text-gray-800">
          {level === null ? "—" : `%${level}`}
          {tank.display?.levelText
            ? ` · ${tank.display.levelText}`
            : tank.lowWater
              ? " · düşük"
              : ""}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-[11px] text-gray-500">Depo</div>
        <div
          className={`text-sm font-Bold ${
            tank.waterTankActive === false
              ? "text-gray-500"
              : tank.tankEmpty
                ? "text-rose-700"
                : "text-emerald-700"
          }`}
        >
          {tank.display
            ? tank.display.warning || "Normal"
            : tank.waterTankActive === false
              ? "Su kapalı"
              : tank.tankEmpty
                ? "Boş"
                : "Dolu"}
          {tank.waterTankPumpMode ? ` · ${tank.waterTankPumpMode}` : ""}
        </div>
      </div>
    </div>
  );
}

/**
 * Bakım → Canlı Durum. Mobil uygulamadaki cihaz kartının panel karşılığı.
 */
export default function OperationStatusPanel({
  live,
  online,
  hasTurkishCoffee,
  hasTeaFilter,
}) {
  const { status, error, data, updatedAt, watchingSince, refreshing, refresh } =
    live;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const cups = Array.isArray(data.cups) ? data.cups : [];
  const pots = data.pots && typeof data.pots === "object" ? data.pots : null;
  const potKeys = pots ? Object.keys(pots).sort() : [];
  const hasAny = cups.length > 0 || potKeys.length > 0 || !!data.waterTank;
  const noDataTimedOut =
    !hasAny && watchingSince && now - watchingSince > NO_DATA_AFTER_MS;

  return (
    <SectionCard
      icon={FaHeartbeat}
      iconBg="bg-teal-500"
      title="CANLI DURUM"
      right={
        <>
          {updatedAt && (
            <span className="hidden sm:inline text-xs text-gray-500">
              Son değişiklik {fmtAgo(updatedAt, now)}
            </span>
          )}
          <LiveBadge
            state={
              hasAny && !noDataTimedOut
                ? "live"
                : status === "error" || noDataTimedOut
                  ? "off"
                  : "waiting"
            }
          />
          <button
            type="button"
            onClick={refresh}
            disabled={status !== "watching" || refreshing}
            title="Makineden güncel durumu iste"
            className={BTN_SECONDARY}
          >
            <FaSyncAlt
              className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
            />
            <span className="hidden md:inline">Yenile</span>
          </button>
        </>
      }
    >
      {online === false && (
        <Notice
          icon={FaPowerOff}
          tone="rose"
          title="Makine çevrimdışı görünüyor"
        >
          Canlı durum makine bağlanınca gelir.
        </Notice>
      )}
      {status === "error" && (
        <Notice
          icon={FaExclamationTriangle}
          tone="rose"
          title="Canlı durum izlenemiyor"
        >
          {error}
        </Notice>
      )}
      {status !== "error" && !hasAny && !noDataTimedOut && (
        // Makineden ilk durum beklenirken kart iskeleti (aktivite)
        <CardGridSkeleton count={hasTurkishCoffee ? 4 : 3} />
      )}
      {status !== "error" && noDataTimedOut && (
        <Notice
          icon={FaExclamationTriangle}
          tone="amber"
          title="Makineden durum gelmedi"
        >
          Makine çevrimdışı olabilir ya da makinede Operasyon Modu kapalı —
          Operasyon Modu kapalıyken makine panele veri göndermez.
        </Notice>
      )}

      {hasTeaFilter && <WaterTankStrip tank={data.waterTank} />}

      {hasTeaFilter && potKeys.length > 0 && (
        <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(230px,1fr))]">
          {potKeys.map((k) => (
            <PotCard
              key={k}
              potKey={k}
              pot={pots[k]}
              lowWater={!!data.waterTank?.lowWater}
              potProgramInfo={data.potProgramInfo}
              now={now}
            />
          ))}
        </div>
      )}

      {hasTurkishCoffee && <TurkishTanksStrip tanks={data.tanks} />}

      {hasTurkishCoffee && cups.length > 0 && (
        <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(230px,1fr))]">
          {cups.map((cup, i) => (
            <CupCard key={i} index={i + 1} cup={cup || {}} now={now} />
          ))}
        </div>
      )}

      <p className="text-[11px] text-gray-400">
        Açılışta makineden tam durum istenir; sonrasında yalnız değişiklikler
        gelir. Makinede Operasyon Modu kapalıysa hiçbir veri gelmez.
      </p>
    </SectionCard>
  );
}
